import {
  Bot,
  Brain,
  BellRing,
  Building,
  BriefcaseBusiness,
  ChartColumn,
  CircleUserRound,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Crosshair,
  Crown,
  FileText,
  FlaskConical,
  Globe,
  Layers,
  Layers3,
  LayoutGrid,
  LayoutTemplate,
  Map,
  Megaphone,
  MessagesSquare,
  Package,
  Palette,
  PanelsTopLeft,
  Plug,
  RotateCcw,
  Scale,
  ScrollText,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  SquarePlay,
  TrendingUp,
  Truck,
  Undo2,
  UserCog,
  Users,
  UsersRound,
  Wallet,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { Permission } from "@digisubs/shared";

// Sidebar navigation in the order and wording of the reference screenshots. "Comptes Meta" is "Comptes publicitaires"
// (Meta + TikTok + Google accounts); "Équipe" opens the members tab of the organisation.

export type BadgeKey = "conversationsUnread" | "ordersToConfirm" | "adAlertsOpen" | "recommendationsOpen";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  permission?: Permission;
  badge?: BadgeKey;
  isNew?: boolean;
  platformAdminOnly?: boolean;
  /** Query string the item adds to its path (e.g. "tab=membres"); the item is active only with it. */
  search?: string;
  /** Extra path prefixes that keep the item highlighted. */
  match?: string[];
}

export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    id: "dashboard",
    title: "Tableau de bord",
    items: [{ label: "Accueil", to: "/", icon: LayoutGrid, permission: "dashboard.view" }],
  },
  {
    id: "media-buyer",
    title: "Media Buyer",
    items: [
      { label: "Vue d'ensemble", to: "/media-buyer", icon: Megaphone, permission: "ads.view" },
      { label: "Campagnes", to: "/media-buyer/campagnes", icon: Layers, permission: "ads.view" },
      { label: "Ensembles de publicités", to: "/media-buyer/ensembles", icon: Layers3, permission: "ads.view" },
      { label: "Publicités", to: "/media-buyer/publicites", icon: SquarePlay, permission: "ads.view" },
      { label: "Créations", to: "/media-buyer/creations", icon: Palette, permission: "ads.view" },
      { label: "Audiences", to: "/media-buyer/audiences", icon: Users, permission: "ads.view" },
      { label: "Performance", to: "/media-buyer/performance", icon: TrendingUp, permission: "ads.view" },
      { label: "AI Media Buyer", to: "/media-buyer/ia", icon: Bot, permission: "ads.view" },
      { label: "Recommandations", to: "/media-buyer/recommandations", icon: ClipboardCheck, permission: "ads.view", badge: "recommendationsOpen" },
      { label: "Expérimentations", to: "/media-buyer/experimentations", icon: FlaskConical, permission: "ads.view" },
      { label: "Décisions", to: "/media-buyer/decisions", icon: Scale, permission: "ads.view" },
      { label: "Alertes publicité", to: "/media-buyer/alertes", icon: BellRing, permission: "ads.view", badge: "adAlertsOpen" },
      { label: "Comptes publicitaires", to: "/media-buyer/comptes", icon: CircleUserRound, permission: "ads.view" },
      { label: "Profil business", to: "/media-buyer/profil-business", icon: BriefcaseBusiness, permission: "ads.view" },
      { label: "Garde-fous", to: "/media-buyer/garde-fous", icon: ShieldCheck, permission: "ads.view" },
      { label: "Audit", to: "/audit", icon: ScrollText, permission: "audit.view" },
    ],
  },
  {
    id: "commerce",
    title: "E-commerce",
    items: [
      { label: "Conversations", to: "/conversations", icon: MessagesSquare, permission: "conversations.view", badge: "conversationsUnread" },
      { label: "Clients", to: "/clients", icon: UsersRound, permission: "customers.view" },
      { label: "Produits", to: "/produits", icon: Package, permission: "products.view" },
      { label: "Commandes", to: "/commandes", icon: ShoppingCart, permission: "orders.view", badge: "ordersToConfirm" },
      { label: "Livraisons & suivi", to: "/livraisons", icon: Truck, permission: "shipping.view" },
      { label: "Retours", to: "/retours", icon: Undo2, permission: "orders.view" },
      { label: "Encaissements", to: "/encaissements", icon: Wallet, permission: "payments.view" },
      { label: "Relances", to: "/relances", icon: RotateCcw, permission: "followups.manage" },
      { label: "Playbooks", to: "/playbooks", icon: Workflow, permission: "playbooks.manage" },
      { label: "Marchés", to: "/marches", icon: Map, permission: "markets.manage" },
    ],
  },
  {
    id: "site",
    title: "Site & Landing",
    items: [
      { label: "Constructeur de site", to: "/site", icon: PanelsTopLeft, permission: "sites.view", isNew: true },
      { label: "Modèles", to: "/modeles", icon: LayoutTemplate, permission: "sites.view" },
      { label: "Domaines", to: "/domaines", icon: Globe, permission: "sites.view" },
      { label: "Formulaires & leads", to: "/formulaires", icon: ClipboardList, permission: "sites.view" },
      { label: "Pixel & suivi", to: "/pixel-suivi", icon: Crosshair, permission: "sites.view" },
      { label: "Outils SEO", to: "/seo", icon: Search, permission: "sites.view" },
    ],
  },
  {
    id: "analysis",
    title: "Analyse",
    items: [
      { label: "Analyse", to: "/analyse", icon: ChartColumn, permission: "analytics.view" },
      { label: "Risque", to: "/risque", icon: ShieldAlert, permission: "analytics.view" },
      { label: "Rapports", to: "/rapports", icon: FileText, permission: "analytics.view" },
    ],
  },
  {
    id: "ai",
    title: "IA",
    items: [
      { label: "Insights IA", to: "/ia/insights", icon: Sparkles, permission: "analytics.view" },
      { label: "Business Brain", to: "/ia/business-brain", icon: Brain, permission: "ai.use" },
    ],
  },
  {
    id: "integrations",
    title: "Intégrations",
    items: [{ label: "Intégrations", to: "/integrations", icon: Plug, permission: "dashboard.view" }],
  },
  {
    id: "system",
    title: "Système",
    items: [
      { label: "Équipe", to: "/organisation", search: "tab=membres", icon: UserCog, permission: "dashboard.view" },
      { label: "Organisation", to: "/organisation", icon: Building, permission: "dashboard.view" },
      { label: "Abonnement", to: "/abonnement", icon: CreditCard, permission: "dashboard.view" },
      { label: "Réglages", to: "/reglages", icon: Settings },
      { label: "Admin plateforme", to: "/admin", icon: Crown, platformAdminOnly: true },
    ],
  },
];

/** Link target of an item (path plus its query string). */
export function navHref(item: NavItem): string {
  return item.search ? `${item.to}?${item.search}` : item.to;
}

export function isNavItemActive(item: NavItem, pathname: string, search = ""): boolean {
  const params = new URLSearchParams(search);
  if (item.search) {
    const [key, value] = item.search.split("=");
    return pathname === item.to && params.get(key!) === value;
  }
  if (item.to === "/") return pathname === "/";
  if (item.to === "/media-buyer") return pathname === "/media-buyer";
  if (item.to === "/organisation" && params.get("tab") === "membres") return false;
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
  return item.match?.some((m) => pathname.startsWith(m)) ?? false;
}
